import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import Content from "./components/content.jsx";
import {Card} from "../../../components/card/card.jsx";
import {subMonths} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import * as htmlToImage from "html-to-image";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 3),
    dtEnd: new Date(),
    users: [],
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    users: "Assumido por",
};

const SosManagement = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);
    const [mapImage, setMapImage] = useState(null);
    const [showMap, setShowMap] = useState(true);
    const [mapMode, setMapMode] = useState("marker");
    const [mapLoaded, setMapLoaded] = useState(false);

    const [title] = useState("Gestão do SOS");
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null);
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                users: filters.users?.map(i => i.value) ?? []
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
                users: filters.users?.map(i => i.label) ?? null
            }, filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/sos_management/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.log("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);
    const hasReportData = reportData && (reportData.apply?.locations?.length > 0 || reportData.apply?.user_analisys?.length > 0 || Object.values(reportData.actionsDistribution ?? {}).some(items => items?.length > 0));
    const hasLocations = reportData?.apply?.locations?.some(location => location.details?.length > 0);

    const canPrint = !hasLocations || mapLoaded;

    const handlePrintAsImage = () => {
        if (!hasLocations) {
            printComponentRef.current?.print();
            return;
        }

        ReportUtils.generateMapImage({
            nodeId: "map-report-component-sos-management",
            setMapImage,
            setShowMap,
            printRef: printComponentRef,
            htmlToImageLib: htmlToImage,
            ignoreElements: (element) => element.tagName === "IFRAME",
            preserveMapStyle: true,
            cacheBust: false
        });
    };


    return (
        <>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                printImg={mapMode === "heatmap"}
                                onPrintImg={handlePrintAsImage}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={(hasReportData ? 1 : 0)}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                                setCollapsed={setCollapsed}
                                canPrint={canPrint}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || (hasReportData ? 1 : 0) === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || (hasReportData ? 1 : 0) === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || (hasReportData ? 1 : 0) === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={(hasReportData ? 1 : 0)}/>
                            <Content reportData={reportData} data={(hasReportData ? 1 : 0)} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} showMap={showMap} mapImage={mapImage} mapMode={mapMode} setMapMode={setMapMode} setMapLoaded={setMapLoaded}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </>
    );
};

export default SosManagement;